import React from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Phone, Mail, MessageCircle, Instagram, MapPin } from "lucide-react";
import { SITE, COURSES } from "@/site";
import SEO, { localBusinessSchema } from "@/components/SEO";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const START_TIMES = [
    "Immediately",
    "This week",
    "Next week",
    "Within a month",
    "Just exploring",
];

export default function Contact() {
    const navigate = useNavigate();
    const [form, setForm] = React.useState({
        name: "",
        phone: "",
        email: "",
        course: "",
        start_time: "",
        message: "",
    });
    const [submitting, setSubmitting] = React.useState(false);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.phone.trim()) {
            toast.error("Name and phone are required.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/leads`, {
                name: form.name.trim(),
                phone: form.phone.trim(),
                email: form.email.trim() || null,
                course: form.course || null,
                start_time: form.start_time || null,
                message: form.message.trim() || null,
            });
            navigate("/thank-you");
        } catch (err) {
            const msg = err?.response?.data?.detail || "Something went wrong. Please try WhatsApp instead.";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="page-contact">
            <SEO
                title="Contact Ihao Music House Delhi | Book Free Trial Class Class"
                description="Book a free demo class at Ihao Music House Delhi — located opposite Select Citywalk, Malviya Nagar. Call +91 88007 47721 or WhatsApp us to enquire."
                path="/contact"
                schema={localBusinessSchema()}
            />
            {/* HERO */}
            <section className="relative py-24 md:py-32 bg-black overflow-hidden">
                <div className="absolute inset-0 bg-radial-gold opacity-60" aria-hidden />
                <div className="absolute inset-0 bg-grid opacity-30" aria-hidden />
                <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <span className="text-[10px] tracking-[0.3em] uppercase text-gold/90">Contact</span>
                    <h1 className="mt-3 font-display text-4xl md:text-6xl font-bold text-white leading-[1.05] max-w-3xl">
                        Book a free demo — and we'll take it from there.
                    </h1>
                    <p className="mt-5 text-white/65 text-base md:text-lg max-w-2xl">
                        Fill the form below or reach us directly. Our team typically responds within a few hours.
                    </p>
                </div>
            </section>

            {/* CONTACT + FORM */}
            <section className="relative py-16 md:py-24 bg-background">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-5 gap-10">
                    {/* Left info */}
                    <div className="lg:col-span-2 flex flex-col gap-6">
                        <div className="rounded-3xl border border-white/10 bg-[#121212] p-8">
                            <h3 className="font-display text-2xl text-white font-semibold">Studio Details</h3>
                            <ul className="mt-6 space-y-5 text-sm">
                                <li className="flex items-start gap-3"><MapPin className="w-4 h-4 text-gold mt-1 shrink-0" /><a href={SITE.mapLink} target="_blank" rel="noreferrer" className="text-white/80 hover:text-gold">{SITE.address}</a></li>
                                <li className="flex items-center gap-3"><Phone className="w-4 h-4 text-gold shrink-0" /><a href={`tel:${SITE.phone}`} className="text-white/80 hover:text-gold">{SITE.phoneIntl}</a></li>
                                <li className="flex items-center gap-3"><Mail className="w-4 h-4 text-gold shrink-0" /><a href={`mailto:${SITE.email}`} className="text-white/80 hover:text-gold break-all">{SITE.email}</a></li>
                                <li className="flex items-center gap-3"><MessageCircle className="w-4 h-4 text-gold shrink-0" /><a href={SITE.whatsapp} target="_blank" rel="noreferrer" className="text-white/80 hover:text-gold">WhatsApp us directly</a></li>
                                <li className="flex items-center gap-3"><Instagram className="w-4 h-4 text-gold shrink-0" /><a href={SITE.instagram} target="_blank" rel="noreferrer" className="text-white/80 hover:text-gold">@ihaomusichouse_delhi</a></li>
                            </ul>
                        </div>
                        <div className="rounded-3xl border border-white/10 overflow-hidden">
                            <iframe title="map" src={SITE.mapEmbed} className="w-full h-64 grayscale contrast-125" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
                        </div>
                    </div>

                    {/* Form */}
                    <form
                        data-testid="lead-form"
                        onSubmit={submit}
                        className="lg:col-span-3 rounded-3xl border border-gold/30 bg-gradient-to-br from-[#1a1405] to-[#0a0a0a] p-8 md:p-10"
                    >
                        <div className="flex items-center justify-between mb-8">
                            <div>
                                <span className="text-[10px] tracking-[0.3em] uppercase text-gold">Free Demo Class</span>
                                <h2 className="mt-2 font-display text-2xl md:text-3xl text-white font-semibold">Tell us a little about you.</h2>
                            </div>
                            <span className="hidden sm:inline-block text-[10px] uppercase tracking-[0.2em] text-gold/70 border border-gold/30 rounded-full px-3 py-1">Limited Seats</span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div className="md:col-span-1">
                                <Label htmlFor="name" className="text-xs uppercase tracking-widest text-white/60">Name *</Label>
                                <Input id="name" required data-testid="form-name" value={form.name} onChange={update("name")} placeholder="Your full name" className="mt-2 bg-black/60 border-white/20 text-white placeholder:text-white/30 focus-visible:ring-gold focus-visible:border-gold rounded-xl h-12" />
                            </div>
                            <div className="md:col-span-1">
                                <Label htmlFor="phone" className="text-xs uppercase tracking-widest text-white/60">Phone *</Label>
                                <Input id="phone" required type="tel" data-testid="form-phone" value={form.phone} onChange={update("phone")} placeholder="10-digit number" className="mt-2 bg-black/60 border-white/20 text-white placeholder:text-white/30 focus-visible:ring-gold focus-visible:border-gold rounded-xl h-12" />
                            </div>
                            <div className="md:col-span-2">
                                <Label htmlFor="email" className="text-xs uppercase tracking-widest text-white/60">Email</Label>
                                <Input id="email" type="email" data-testid="form-email" value={form.email} onChange={update("email")} placeholder="you@example.com" className="mt-2 bg-black/60 border-white/20 text-white placeholder:text-white/30 focus-visible:ring-gold focus-visible:border-gold rounded-xl h-12" />
                            </div>
                            <div className="md:col-span-1">
                                <Label className="text-xs uppercase tracking-widest text-white/60">Course</Label>
                                <Select value={form.course} onValueChange={(v) => setForm((f) => ({ ...f, course: v }))}>
                                    <SelectTrigger data-testid="form-course" className="mt-2 bg-black/60 border-white/20 text-white focus:ring-gold focus:border-gold rounded-xl h-12">
                                        <SelectValue placeholder="Select a course" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#121212] border-white/10 text-white">
                                        {COURSES.map((c) => (
                                            <SelectItem key={c.slug} value={c.name} className="focus:bg-gold/20">{c.name}</SelectItem>
                                        ))}
                                        <SelectItem value="Not sure yet" className="focus:bg-gold/20">Not sure yet</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="md:col-span-1">
                                <Label className="text-xs uppercase tracking-widest text-white/60">Start Time</Label>
                                <Select value={form.start_time} onValueChange={(v) => setForm((f) => ({ ...f, start_time: v }))}>
                                    <SelectTrigger data-testid="form-start-time" className="mt-2 bg-black/60 border-white/20 text-white focus:ring-gold focus:border-gold rounded-xl h-12">
                                        <SelectValue placeholder="When would you like to begin?" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#121212] border-white/10 text-white">
                                        {START_TIMES.map((t) => (
                                            <SelectItem key={t} value={t} className="focus:bg-gold/20">{t}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="md:col-span-2">
                                <Label htmlFor="message" className="text-xs uppercase tracking-widest text-white/60">Message</Label>
                                <Textarea id="message" data-testid="form-message" rows={5} value={form.message} onChange={update("message")} placeholder="Tell us about your goals, experience or any questions…" className="mt-2 bg-black/60 border-white/20 text-white placeholder:text-white/30 focus-visible:ring-gold focus-visible:border-gold rounded-xl" />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="form-submit-btn"
                            className="mt-8 w-full sm:w-auto inline-flex items-center justify-center bg-gold text-black font-semibold text-sm px-8 py-4 rounded-full hover:bg-gold-hover hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 gold-glow"
                        >
                            {submitting ? "Submitting…" : "Book Free Trial Class"}
                        </button>
                        <p className="mt-4 text-xs text-white/40">By submitting, you agree to be contacted by Ihao Music House Delhi regarding your enquiry.</p>
                    </form>
                </div>
            </section>
        </div>
    );
}
